import _ from 'lodash';
import { useEffect, useState } from 'react';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { selectSelectedDateValue, useReportStore } from '@/entities/report';
import { getMinMaxHr, sortMinMaxHr } from './handler';
import type { DailySummaryThumbnailProps } from './dailySummaryThumbnail';

/**
 * Drives the per-day thumbnail list of the HR summary tab, handling day selection and the jump-to-min or jump-to-max shortcuts. Rebuilds hrDataDefault plus the min and max beat lists from the selected day whenever the combined summaries change, matching suggested beats to the stored min and max time at second precision and prepending a synthetic entry when none matches, sorts both lists, recomputes the min/max chart bounds, and on day change cancels in-flight requests and clears the modal, column, event and jump state.
 *
 * @param props - Thumbnail configuration, notably the timezone offset used to resolve the initially selected date.
 * @returns The selected date, the combined daily summaries, the modal open flag with its setter, the min and max chart bounds, the thumbnail and view-min/view-max click handlers, and the edited-day predicate.
 */
const useDailySummaryThumbnail = ({ timezoneOffset }: DailySummaryThumbnailProps) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Records whether the selected day carries no ECG data. Forwards the flag to the shared empty-data action, binding it to the tab-6 store key.
   *
   * @param v - The empty-data flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Sets which day the HR summary tab is showing. Forwards the value to the shared selected-date action, binding it to the tab-6 store key.
   *
   * @param v - The ISO date string, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const resetControlModalHrSummary = useReportStore((s) => s.actions.resetControlHrSummaryModal);
  const dailySummariesCombine = useReportStore((s) => s.dailySummariesCombine);
  /**
   * Reads the pristine, server-loaded daily heart-rate summaries. Pulls originalDailySummaries off a fresh store snapshot instead of subscribing, so comparisons run against the live value.
   *
   * @returns The original daily summaries as last loaded from the server.
   */
  const getOriginalDailySummaries = () => useReportStore.getState().originalDailySummaries;
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Replaces the ECG payload currently held for the HR summary tab. Forwards the value to the shared ECG-object action, binding it to the tab-6 store key.
   *
   * @param v - The ECG object, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Restores the tab's control-modal state to its defaults. Invokes the shared reset action against the tab-6 store key.
   *
   * @returns Nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  /**
   * Reads the baseline heart-rate summary the thumbnails start from. Pulls hrDataDefault off a fresh store snapshot rather than subscribing, so effects can clone it without re-running on every store change.
   *
   * @returns The default heart-rate summary for the report.
   */
  const getHrDataDefault = () => useReportStore.getState().hrDataDefault;
  const setHrDataDefault = useReportStore((s) => s.actions.setHrDataDefault);
  const setMinHrsInDate = useReportStore((s) => s.actions.setMinHrsInDate);
  const setMaxHrsInDate = useReportStore((s) => s.actions.setMaxHrsInDate);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the ECG viewer to a given moment in the HR summary tab. Forwards the value to the shared jump-date action, binding it to the tab-6 store key.
   *
   * @param v - The target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time-of-day the ECG viewer jumps to within the selected date. Forwards the value to the shared jump-time action, binding it to the tab-6 store key.
   *
   * @param v - The target time, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Selects which portion of the jumped-to page the ECG viewer scrolls to. Forwards the value to the shared jump-portion action, binding it to the tab-6 store key.
   *
   * @param v - The portion index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Stores the events and points of the currently selected chart column. Forwards the value to the shared selected-column action, binding it to the tab-6 store key.
   *
   * @param v - The selected column's events and points, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the ECG events currently selected in the HR summary tab. Forwards the value to the shared selected-event action, binding it to the tab-6 store key.
   *
   * @param v - The selected events and points, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setPrevColumnInfoAction = useReportStore((s) => s.actions.setPrevColumnInfo);
  /**
   * Stores the events and points belonging to the column before the selected one. Forwards the value to the shared previous-column action, binding it to the tab-6 store key.
   *
   * @param v - The previous column's events and points, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setPrevColumnInfo = (v: Parameters<typeof setPrevColumnInfoAction>[1]) =>
    setPrevColumnInfoAction(keyRecoil, v);
  const setNextColumnInfoAction = useReportStore((s) => s.actions.setNextColumnInfo);
  /**
   * Stores the events and points belonging to the column after the selected one. Forwards the value to the shared next-column action, binding it to the tab-6 store key.
   *
   * @param v - The next column's events and points, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setNextColumnInfo = (v: Parameters<typeof setNextColumnInfoAction>[1]) =>
    setNextColumnInfoAction(keyRecoil, v);
  const setSelectedHrTimeAction = useReportStore((s) => s.actions.setSelectedHrTime);
  /**
   * Marks which beat time the HR summary is currently focused on. Forwards the value to the shared selected-HR-time action, binding it to the tab-6 store key.
   *
   * @param v - The beat timestamp, or undefined to clear the selection.
   * @returns Nothing.
   */
  const setSelectedHrTime = (v: string | undefined) => setSelectedHrTimeAction(keyRecoil, v);
  const setViewHrModeAction = useReportStore((s) => s.actions.setViewHrMode);
  /**
   * Switches the HR viewer between minimum and maximum heart-rate mode. Forwards the mode to the shared view-HR-mode action, binding it to the tab-6 store key.
   *
   * @param v - Either the MAX_HR or MIN_HR rhythm-event mode.
   * @returns Nothing.
   */
  const setViewHrMode = (v: TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR) =>
    setViewHrModeAction(keyRecoil, v);
  const setMinHrTabActive = useReportStore((s) => s.actions.setMinHrTabActive);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [dataMinMaxHr, setDataMinMaxHr] = useState(null);

  /**
   * Switches the HR summary tab to another day when a thumbnail is clicked. Does nothing if the day is already selected, otherwise activates the min-HR tab, cancels in-flight API calls, resets both control modals, stores the new date and its empty flag, and clears the column, event, ECG and jump state.
   *
   * @param dailySummary - The clicked day's summary, supplying its date and empty-data flag.
   * @param index - Position of the thumbnail in the list; accepted but unused.
   * @returns Nothing.
   */
  const onClickThumbnail = (dailySummary: any, index?: any) => {
    if (dailySummary.date !== selectedDateValue) {
      setMinHrTabActive(true);
      cancelAllAPI();
      resetControlModal();
      resetControlModalHrSummary();
      setSelectedDateValue(dailySummary.date);
      setIsEmptyData(dailySummary.isEmpty);
      setSelectedColumnInfo([]);
      setSelectedEventInfo([]);
      setPrevColumnInfo([]);
      setNextColumnInfo([]);
      setEcgObject({});
      setJumpDate(undefined);
      setJumpTime(undefined);
      setJumpPortion(0);
    }
  };

  // This function is used to view the max hr
  /**
   * Jumps the ECG viewer to the maximum heart-rate beat of a given day. Clears the ECG object and switches to that day, then after a one-second delay switches to max-HR view mode, marks the beat time, jumps the viewer to it and deactivates the min-HR tab; days without a maxHr are ignored.
   *
   * @param hrData - The day summary whose maxHr date and time drive the jump.
   * @returns Nothing.
   */
  const onClickViewMaxHr = (hrData: any) => {
    if (hrData.maxHr) {
      setEcgObject({});
      setSelectedDateValue(hrData.maxHr.date);
      setTimeout(() => {
        setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MAX_HR);
        setSelectedHrTime(hrData.maxHr.time);
        setJumpDate(hrData.maxHr.time);
        setJumpPortion(0);
        setMinHrTabActive(false);
      }, 1000);
    }
  };

  // This function is used to view the min hr
  /**
   * Jumps the ECG viewer to the minimum heart-rate beat of a given day. Clears the ECG object and switches to that day, then after a one-second delay switches to min-HR view mode, marks the beat time, jumps the viewer to it and activates the min-HR tab; days without a minHr are ignored.
   *
   * @param hrData - The day summary whose minHr date and time drive the jump.
   * @returns Nothing.
   */
  const onClickViewMinHr = (hrData: any) => {
    if (hrData.minHr) {
      setEcgObject({});
      setSelectedDateValue(hrData.minHr.date);
      setTimeout(() => {
        setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MIN_HR);
        setSelectedHrTime(hrData.minHr.time);
        setJumpDate(hrData.minHr.time);
        setJumpPortion(0);
        setMinHrTabActive(true);
      }, 1000);
    }
  };

  useEffect(() => {
    if (dailySummariesCombine.length) {
      if (!selectedDateValue) {
        //* Store the raw combine date so it stays consistent with the value stored
        //* on thumbnail click, otherwise the date match below can fail after reload.
        setSelectedDateValue(dailySummariesCombine[0].date);
        return;
      }

      const hrDataDefaultObj: any = _.cloneDeep(getHrDataDefault());
      //* Match by instant, not exact string. On reload the persisted selectedDateValue
      //* and the (re-fetched) combine dates can differ in format/timezone, which would
      //* leave the content stuck on a previously computed day while the card shows another.
      const isSameDate = (combineDate: string, selectedDate: string) => {
        if (combineDate === selectedDate) return true;
        if (!combineDate || !selectedDate) return false;
        //* Same instant despite format differences (ms / timezone suffix).
        const timeA = new Date(combineDate).getTime();
        const timeB = new Date(selectedDate).getTime();
        if (!Number.isNaN(timeA) && !Number.isNaN(timeB) && timeA === timeB) return true;
        //* Legacy: values persisted before this fix were stored timezone-shifted
        //* via convertToMomentJs(...).toISOString(); match those too so they self-heal.
        return convertToMomentJs(combineDate, timezoneOffset).toISOString() === selectedDate;
      };
      const foundDailySummary: any = _.find(dailySummariesCombine, (x: any) =>
        isSameDate(x.date, selectedDateValue),
      );
      if (foundDailySummary) {
        hrDataDefaultObj.minHr.value = foundDailySummary.minHr?.value;
        hrDataDefaultObj.minHr.time = foundDailySummary.minHr?.time;
        hrDataDefaultObj.minHr.hrsOnBeat = foundDailySummary.minHr?.hrsOnBeat;
        hrDataDefaultObj.minHr.userUpdated = foundDailySummary.minHr?.userUpdated;
        hrDataDefaultObj.maxHr.value = foundDailySummary.maxHr?.value;
        hrDataDefaultObj.maxHr.time = foundDailySummary.maxHr?.time;
        hrDataDefaultObj.maxHr.hrsOnBeat = foundDailySummary.maxHr?.hrsOnBeat;
        hrDataDefaultObj.maxHr.userUpdated = foundDailySummary.maxHr?.userUpdated;
        hrDataDefaultObj.avgHr.value = foundDailySummary.avgHr?.value;
        hrDataDefaultObj.avgHr.userUpdated = foundDailySummary.avgHr?.userUpdated;

        const minHrs: any[] = _.map(foundDailySummary.suggestHrsOnBeat?.minHr, (x: any) => ({
          value: x.avgHr,
          start: x.head,
          stop: x.tail,
          userUpdated: x?.userUpdated,
        }));

        const maxHrs: any[] = _.map(foundDailySummary.suggestHrsOnBeat?.maxHr, (x: any) => ({
          value: x.avgHr,
          start: x.head,
          stop: x.tail,
          userUpdated: x?.userUpdated,
        }));
        // Fixing: if HR only deviates ms, the format is equal
        const foundMin: any = _.find(minHrs, (x: any) => {
          const startMinutes = new Date(x.start).setMilliseconds(0);
          const minHrMinutes = new Date(foundDailySummary.minHr?.time).setMilliseconds(0);
          return startMinutes === minHrMinutes;
        });
        if (foundMin) {
          foundMin.value = foundDailySummary.minHr?.value;
          foundMin.userUpdated = foundDailySummary.minHr?.userUpdated;
          foundMin.start = foundDailySummary.minHr?.hrsOnBeat?.head;
          foundMin.stop = foundDailySummary.minHr?.hrsOnBeat?.tail;
        } else {
          minHrs.unshift({
            value: foundDailySummary.minHr?.value,
            start: foundDailySummary.minHr?.hrsOnBeat?.head,
            stop: foundDailySummary.minHr?.hrsOnBeat?.tail,
            userUpdated: foundDailySummary.minHr?.userUpdated,
          });
        }
        // Fixing: if HR only deviates ms, the format is equal
        const foundMax: any = _.find(maxHrs, (x: any) => {
          const startMinutes = new Date(x.start).setMilliseconds(0);
          const maxHrMinutes = new Date(foundDailySummary.maxHr?.time).setMilliseconds(0);
          return startMinutes === maxHrMinutes;
        });
        if (foundMax) {
          foundMax.value = foundDailySummary.maxHr?.value;
          foundMax.userUpdated = foundDailySummary.maxHr?.userUpdated;
          foundMax.start = foundDailySummary.maxHr?.hrsOnBeat?.head;
          foundMax.stop = foundDailySummary.maxHr?.hrsOnBeat?.tail;
        } else {
          maxHrs.unshift({
            value: foundDailySummary.maxHr?.value,
            start: foundDailySummary.maxHr?.hrsOnBeat?.head,
            stop: foundDailySummary.maxHr?.hrsOnBeat?.tail,
            userUpdated: foundDailySummary.maxHr?.userUpdated,
          });
        }

        const sortedMinHrs = sortMinMaxHr(minHrs, 'minHrs');
        const sortedMaxHrs = sortMinMaxHr(maxHrs, 'maxHrs');

        setMinHrsInDate(sortedMinHrs);
        setMaxHrsInDate(sortedMaxHrs);
      } else {
        //* Selected day is not (yet) in combine: never leave another day's Min/Max HR
        //* content on screen. Clearing shows the no-data state until the correct day
        //* resolves, instead of silently displaying the wrong day's values.
        setMinHrsInDate([]);
        setMaxHrsInDate([]);
      }
      setHrDataDefault(hrDataDefaultObj);
    }
    const minMaxData = getMinMaxHr(dailySummariesCombine);
    setDataMinMaxHr(minMaxData);
  }, [dailySummariesCombine, selectedDateValue]);

  /**
   * Reports whether a day's heart-rate summary has drifted from the version originally loaded from the server, so the thumbnail can be flagged as edited. Looks the day up in the original summaries by date and compares the value and time of minHr, maxHr and avgHr, returning false when no original row exists.
   *
   * @param hrSummaryDate - The day summary to compare, carrying its date plus min, max and average heart rate.
   * @returns True when any min, max or average value or time differs from the original.
   */
  const isValueDifferentFromOrigin = (hrSummaryDate: any) => {
    const originalDailySummaries = getOriginalDailySummaries();
    const foundOriginDailySummary: any = _.find(
      originalDailySummaries,
      (x: any) => x.date === hrSummaryDate.date,
    );
    if (foundOriginDailySummary) {
      const {
        minHr: originMinHr,
        maxHr: originMaxHr,
        avgHr: originAvgHr,
      } = foundOriginDailySummary;
      const { minHr: newMinHr, maxHr: newMaxHr, avgHr: newAvgHr } = hrSummaryDate;
      return (
        originMinHr?.value !== newMinHr?.value ||
        originMinHr?.time !== newMinHr?.time ||
        originMaxHr?.value !== newMaxHr?.value ||
        originMaxHr?.time !== newMaxHr?.time ||
        originAvgHr?.value !== newAvgHr?.value ||
        originAvgHr?.time !== newAvgHr?.time
      );
    }
    return false;
  };

  return {
    selectedDateValue,
    dailySummariesCombine,
    isOpenModal,
    setIsOpenModal,
    dataMinMaxHr,
    onClickThumbnail,
    onClickViewMaxHr,
    onClickViewMinHr,
    isValueDifferentFromOrigin,
  };
};

export default useDailySummaryThumbnail;
